/**
 * Nested panel shown when an attempt row is expanded: filter tabs + a table of the attempt's
 * actions. Rows carrying before/after JSON expose a GitHub-style diff.
 */
import { useMemo, useState } from 'react';

import { IStoreCopyAction } from '../../../../../services/api/troubleshooting-service';
import { ActionCategory, buildActionTabs, filterActions } from '../action-categories';
import ActionFilterTabs from './action-filter-tabs';
import JsonDiffView from './json-diff-view';

interface IAttemptActionsPanelProps {
    actions: IStoreCopyAction[];
}

const ACTION_LABELS: Record<string, string> = {
    created: 'Created',
    updated: 'Updated',
    integration_linked: 'Integration linked',
    integration_not_found: 'Integration not found',
    disabled: 'Disabled',
    soft_deleted: 'Soft deleted',
};

const AttemptActionsPanel: React.FC<IAttemptActionsPanelProps> = ({ actions }) => {
    const tabs = useMemo(() => buildActionTabs(actions), [actions]);
    const [active, setActive] = useState<ActionCategory>(
        // Default to "Requires attention" when it has items, otherwise "All".
        tabs.find((t) => t.id === 'requires_attention' && t.count > 0) ? 'requires_attention' : 'all',
    );

    const visibleActions = useMemo(() => filterActions(actions, active), [actions, active]);

    if (actions.length === 0) {
        return <p className="attempt-actions__empty">No actions were recorded for this attempt.</p>;
    }

    return (
        <div className="attempt-actions">
            <ActionFilterTabs tabs={tabs} active={active} onChange={setActive} />

            {visibleActions.length === 0 ? (
                <p className="attempt-actions__empty">No actions in this category.</p>
            ) : (
                <div className="attempt-actions__table">
                    <div className="attempt-actions__row attempt-actions__row--head">
                        <span>Entity</span>
                        <span>Action</span>
                        <span>Source → New</span>
                        <span>Details</span>
                    </div>
                    {visibleActions.map((action) => {
                        const hasDiff = action.before_json != null || action.after_json != null;
                        return (
                            <div key={action.id} className="attempt-actions__row">
                                <span className="attempt-actions__entity">{action.entity_type}</span>
                                <span>
                                    <span className={`action-badge action-badge--${action.action}`}>
                                        {ACTION_LABELS[action.action] ?? action.action}
                                    </span>
                                </span>
                                <span className="attempt-actions__ids">
                                    {action.source_id ?? '—'} → {action.new_id ?? '—'}
                                </span>
                                <span className="attempt-actions__details">
                                    {action.message && (
                                        <p className="attempt-actions__message">{action.message}</p>
                                    )}
                                    {hasDiff && (
                                        <JsonDiffView beforeJson={action.before_json} afterJson={action.after_json} />
                                    )}
                                    {!action.message && !hasDiff && <span className="attempt-actions__muted">—</span>}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AttemptActionsPanel;
